import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Send, Globe } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INFO = [
  { icon: Mail, label: "Email", value: "kumar.edu12vk@gmail.com", href: "mailto:kumar.edu12vk@gmail.com", id: "email" },
  { icon: Phone, label: "Phone", value: "+91 98880 01772", href: "tel:+919888001772", id: "phone" },
  { icon: Globe, label: "Portfolio", value: "passionatedesigner.co.in", href: "https://passionatedesigner.co.in", id: "website" },
  { icon: MapPin, label: "Based in", value: "Chandigarh, India", href: null, id: "location" },
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thank you for reaching out. I'll get back to you soon.");
      setForm({ name: "", email: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again or email me directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-20 lg:py-24 border-t border-[#222634]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
          <div>
            <p className="font-mono2 text-xs tracking-[0.3em] uppercase text-[#FF4D23] mb-3">06 · Let&apos;s Connect</p>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight uppercase leading-tight mb-6" data-testid="contact-heading">
              Looking for a <span className="text-[#FF4D23]">Design Leader</span> Who Still Loves the Craft?
            </h2>
            <p className="text-[#B6BDCB] text-base leading-relaxed mb-10 max-w-md">
              I&apos;m open to senior design and creative leadership opportunities where brand thinking,
              visual craft and team collaboration come together. If that sounds relevant to your team, let&apos;s connect.
            </p>
            <div className="space-y-4">
              {INFO.map((i) => (
                <a
                  key={i.id}
                  href={i.href || "#contact"}
                  {...(i.href?.startsWith("http") ? { target: "_blank", rel: "noreferrer" } : {})}
                  data-testid={`contact-info-${i.id}`}
                  className={`flex items-center gap-4 bg-[#12141B] border border-[#222634] rounded-xl p-5 ${i.href ? "hover:border-[#FF4D23] transition-colors" : "cursor-default"}`}
                >
                  <i.icon size={20} className="text-[#FF4D23] shrink-0" />
                  <div>
                    <p className="text-[10px] font-mono2 tracking-widest uppercase text-[#8A92A3]">{i.label}</p>
                    <p className="text-sm sm:text-base font-semibold text-[#F5F6FA]">{i.value}</p>
                  </div>
                </a>
              ))}
            </div>
          </div>
          <form onSubmit={submit} className="bg-[#12141B] border border-[#222634] rounded-2xl p-6 sm:p-8 space-y-5" data-testid="contact-form">
            <div>
              <label className="text-xs font-mono2 tracking-widest uppercase text-[#8A92A3] block mb-2">Your Name</label>
              <input
                data-testid="contact-input-name"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full bg-[#0A0B0E] border border-[#3B4254] rounded-lg px-4 py-3 text-sm text-[#F5F6FA] focus:outline-none focus:border-[#FF4D23] transition-colors"
                placeholder="Enter your name"
              />
            </div>
            <div>
              <label className="text-xs font-mono2 tracking-widest uppercase text-[#8A92A3] block mb-2">Your Email</label>
              <input
                data-testid="contact-input-email"
                required
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full bg-[#0A0B0E] border border-[#3B4254] rounded-lg px-4 py-3 text-sm text-[#F5F6FA] focus:outline-none focus:border-[#FF4D23] transition-colors"
                placeholder="Enter your email address"
              />
            </div>
            <div>
              <label className="text-xs font-mono2 tracking-widest uppercase text-[#8A92A3] block mb-2">Message</label>
              <textarea
                data-testid="contact-input-message"
                required
                rows={5}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                className="w-full bg-[#0A0B0E] border border-[#3B4254] rounded-lg px-4 py-3 text-sm text-[#F5F6FA] focus:outline-none focus:border-[#FF4D23] transition-colors resize-none"
                placeholder="Tell me about the role, project or opportunity."
              />
            </div>
            <button
              type="submit"
              disabled={sending}
              data-testid="contact-submit-button"
              className="w-full inline-flex items-center justify-center gap-2 min-h-[44px] bg-[#FF4D23] text-[#0A0B0E] font-bold px-7 py-3.5 rounded-full hover:bg-[#ff6a45] transition-colors disabled:opacity-60"
            >
              {sending ? "Sending..." : "Send Message"} <Send size={16} />
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}
